import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate } from "@/lib/utils-format";
import { Plus, Trash2, ChefHat, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";
import { QuickAddIngredient } from "@/components/quick-add";

const UNITS = ["pcs", "kg", "g", "dozen", "box"];
const emptyIngRow = { ingredient_id: "", quantity: 0, unit: "g" };
const emptyRecipe = { yield_qty: 1, yield_unit: "pcs", ingredients: [{ ...emptyIngRow }], process_notes: "", labor_cost: 0, overhead_cost: 0, packaging_cost: 0 };

export default function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [search, setSearch] = useState("");
  const [editorFor, setEditorFor] = useState(null); // product object
  const [recipe, setRecipe] = useState(emptyRecipe);

  const load = async () => {
    const [r, p, i] = await Promise.all([api.get("/recipes"), api.get("/products"), api.get("/ingredients")]);
    setRecipes(r.data); setProducts(p.data); setIngredients(i.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return !s ? recipes : recipes.filter((r) => (r.product_name || "").toLowerCase().includes(s));
  }, [recipes, search]);

  const productsWithoutRecipe = products.filter((p) => !recipes.find((r) => r.product_id === p.id));

  // Aggregate stats
  const stats = useMemo(() => {
    if (!recipes.length) return null;
    const avgMargin = recipes.reduce((s, r) => s + Number(r.margin_pct || 0), 0) / recipes.length;
    const lowMargin = recipes.filter((r) => Number(r.margin_pct) < 30).length;
    const negMargin = recipes.filter((r) => Number(r.margin_pct) < 0).length;
    return { count: recipes.length, avgMargin, lowMargin, negMargin };
  }, [recipes]);

  const openEditor = async (product) => {
    setEditorFor(product);
    const { data } = await api.get(`/products/${product.id}`);
    if (data.recipe) {
      setRecipe({
        yield_qty: data.recipe.yield_qty || 1,
        yield_unit: data.recipe.yield_unit || "pcs",
        ingredients: data.recipe.ingredients.map((i) => ({ ingredient_id: i.ingredient_id, quantity: i.quantity, unit: i.unit })),
        process_notes: data.recipe.process_notes || "",
        labor_cost: data.recipe.labor_cost || 0,
        overhead_cost: data.recipe.overhead_cost || 0,
        packaging_cost: data.recipe.packaging_cost || 0,
      });
    } else {
      setRecipe(emptyRecipe);
    }
  };

  const saveRecipe = async () => {
    const valid = recipe.ingredients.filter((r) => r.ingredient_id && r.quantity > 0);
    if (valid.length === 0) return toast.error("Add at least one ingredient");
    try {
      const { data } = await api.put(`/products/${editorFor.id}/recipe`, {
        yield_qty: Number(recipe.yield_qty) || 1,
        yield_unit: recipe.yield_unit,
        ingredients: valid.map((r) => ({ ingredient_id: r.ingredient_id, quantity: Number(r.quantity), unit: r.unit })),
        process_notes: recipe.process_notes,
        labor_cost: Number(recipe.labor_cost) || 0,
        overhead_cost: Number(recipe.overhead_cost) || 0,
        packaging_cost: Number(recipe.packaging_cost) || 0,
      });
      toast.success(`Saved · Cost/unit ${inr(data.cost_per_unit)} · Margin ${data.margin_pct}%`);
      setEditorFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeRecipe = async (r) => {
    if (!window.confirm(`Delete recipe for ${r.product_name}?`)) return;
    await api.delete(`/products/${r.product_id}/recipe`);
    toast.success("Deleted"); load();
  };

  const addIngRow = () => setRecipe((r) => ({ ...r, ingredients: [...r.ingredients, { ...emptyIngRow }] }));
  const removeIngRow = (i) => setRecipe((r) => ({ ...r, ingredients: r.ingredients.filter((_, x) => x !== i) }));
  const updateIngRow = (i, k, v) => setRecipe((r) => {
    const arr = [...r.ingredients];
    arr[i] = { ...arr[i], [k]: v };
    return { ...r, ingredients: arr };
  });

  // Live cost preview
  const previewIngCost = recipe.ingredients.reduce((sum, row) => {
    const ing = ingredients.find((x) => x.id === row.ingredient_id);
    if (!ing) return sum;
    return sum + Number(row.quantity || 0) * Number(ing.cost_per_unit || 0);
  }, 0);
  const previewProdCost = Number(recipe.labor_cost || 0) + Number(recipe.overhead_cost || 0) + Number(recipe.packaging_cost || 0);
  const previewTotal = previewIngCost + previewProdCost;
  const previewPerUnit = previewTotal / Math.max(Number(recipe.yield_qty) || 1, 1);
  const previewMargin = editorFor ? (Number(editorFor.sale_price) || 0) - previewPerUnit : 0;
  const previewMarginPct = editorFor && editorFor.sale_price ? (previewMargin / editorFor.sale_price) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Kitchen</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <ChefHat className="w-8 h-8 text-[#C05621]" /> Recipes & Costing
          </h1>
          <p className="text-sm text-[#5C4033] mt-1">Ingredient cost + Production cost = Product cost. Live margin against sale price.</p>
        </div>
        {productsWithoutRecipe.length > 0 && (
          <div className="flex items-center gap-2">
            <Select onValueChange={(v) => { const p = products.find((x) => x.id === v); if (p) openEditor(p); }}>
              <SelectTrigger className="w-64" data-testid="recipes-add-select"><SelectValue placeholder={`+ Recipe for a product (${productsWithoutRecipe.length} pending)`} /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {productsWithoutRecipe.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · SKU {p.sku}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <NewRecipeButton products={products} recipes={recipes} ingredients={ingredients} onPick={openEditor} onProductsChanged={load} onLoad={load} />
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Recipes" value={stats.count} />
          <Stat label="Avg. Margin %" value={`${stats.avgMargin.toFixed(1)}%`} accent="text-[#C05621]" />
          <Stat label="Low-margin (<30%)" value={stats.lowMargin} accent={stats.lowMargin ? "text-amber-700" : "text-emerald-700"} />
          <Stat label="Negative-margin" value={stats.negMargin} accent={stats.negMargin ? "text-red-700" : "text-emerald-700"} />
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-[#EAE5D9] rounded-md px-3 py-2 max-w-md">
        <Search className="w-4 h-4 text-[#8B7355]" />
        <input placeholder="Search recipes…" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="recipes-search" />
      </div>

      {/* Table */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3 text-right">Yield</th>
              <th className="px-4 py-3 text-right">Ingredient Cost</th>
              <th className="px-4 py-3 text-right">Production Cost</th>
              <th className="px-4 py-3 text-right">Total Cost / Batch</th>
              <th className="px-4 py-3 text-right">Cost / Unit</th>
              <th className="px-4 py-3 text-right">Sale Price</th>
              <th className="px-4 py-3 text-right">Margin</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const negMargin = Number(r.margin_pct) < 0;
              const lowMargin = Number(r.margin_pct) < 30;
              return (
                <tr key={r.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7] cursor-pointer" data-testid={`recipe-row-${r.product_id}`}>
                  <td className="px-4 py-3 font-medium" onClick={() => { const p = products.find((x) => x.id === r.product_id); if (p) openEditor(p); }}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-[#FEEBC8] text-[#C05621] flex items-center justify-center"><ChefHat className="w-4 h-4" /></div>
                      <div>
                        <div>{r.product_name}</div>
                        <div className="text-xs text-[#8B7355]">Updated {fmtDate(r.updated_at)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{r.yield_qty} {r.yield_unit}</td>
                  <td className="px-4 py-3 text-right">{inr(r.ingredient_cost || 0)}</td>
                  <td className="px-4 py-3 text-right text-[#5C4033]">{inr(r.production_cost || 0)}</td>
                  <td className="px-4 py-3 text-right font-medium">{inr(r.total_cost || 0)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#C05621]">{inr(r.cost_per_unit || 0)}</td>
                  <td className="px-4 py-3 text-right">{inr(r.product_sale_price || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className={`font-semibold ${negMargin ? "text-red-700" : lowMargin ? "text-amber-700" : "text-emerald-700"}`}>
                      {inr(r.margin || 0)}
                    </div>
                    <div className={`text-xs ${negMargin ? "text-red-600" : lowMargin ? "text-amber-600" : "text-emerald-600"}`}>
                      {Number(r.margin_pct || 0).toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">v{r.version}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => removeRecipe(r)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[#8B7355]">
                {recipes.length === 0 ? (
                  <>No recipes yet. {productsWithoutRecipe.length > 0 ? "Pick a product above to add its first recipe." : "Add a product first."}</>
                ) : "No recipes match your search."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Editor */}
      <Dialog open={!!editorFor} onOpenChange={(o) => !o && setEditorFor(null)}>
        <DialogContent className="max-w-4xl bg-white max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-[#C05621]" /> Recipe · {editorFor?.name}
              {editorFor?.sku && <span className="text-xs text-[#8B7355] font-mono">SKU {editorFor.sku}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yield Quantity (per batch)</Label><Input type="number" value={recipe.yield_qty} onChange={(e) => setRecipe({ ...recipe, yield_qty: e.target.value })} data-testid="recipes-yield-input" /></div>
              <div>
                <Label>Yield Unit</Label>
                <Select value={recipe.yield_unit} onValueChange={(v) => setRecipe({ ...recipe, yield_unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="uc-label">Ingredients (BOM)</div>
                <Button size="sm" variant="outline" onClick={addIngRow} data-testid="recipes-add-ing"><Plus className="w-3 h-3 mr-1" /> Add ingredient</Button>
                <QuickAddIngredient label="Quick add master" onAdded={async (ing) => { await load(); setRecipe((r) => { const arr = [...r.ingredients]; const emptyIdx = arr.findIndex((x) => !x.ingredient_id); if (emptyIdx >= 0) arr[emptyIdx] = { ingredient_id: ing.id, quantity: 0, unit: ing.unit || "g" }; else arr.push({ ingredient_id: ing.id, quantity: 0, unit: ing.unit || "g" }); return { ...r, ingredients: arr }; }); }} />
              </div>
              <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F7F2E8] uc-label">
                    <tr>
                      <th className="text-left px-2 py-2">Ingredient</th>
                      <th className="text-right px-2 py-2 w-24">Qty</th>
                      <th className="text-left px-2 py-2 w-24">Unit</th>
                      <th className="text-right px-2 py-2 w-28">Cost</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.ingredients.map((row, i) => {
                      const ing = ingredients.find((x) => x.id === row.ingredient_id);
                      const cost = ing ? Number(ing.cost_per_unit) * Number(row.quantity || 0) : 0;
                      return (
                        <tr key={i} className="border-t border-[#EAE5D9]">
                          <td className="px-2 py-1.5">
                            <Select value={row.ingredient_id} onValueChange={(v) => updateIngRow(i, "ingredient_id", v)}>
                              <SelectTrigger className="h-8" data-testid={`recipes-ing-select-${i}`}><SelectValue placeholder="Select ingredient" /></SelectTrigger>
                              <SelectContent className="bg-white max-h-64">
                                {ingredients.map((ing) => <SelectItem key={ing.id} value={ing.id}>{ing.name} · {inr(ing.cost_per_unit)}/{ing.unit}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5"><Input type="number" value={row.quantity} onChange={(e) => updateIngRow(i, "quantity", e.target.value)} className="h-8 text-right" /></td>
                          <td className="px-2 py-1.5">
                            <Select value={row.unit} onValueChange={(v) => updateIngRow(i, "unit", v)}>
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-white">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5 text-right font-medium">{inr(cost)}</td>
                          <td className="px-2 py-1.5">
                            {recipe.ingredients.length > 1 && (
                              <button onClick={() => removeIngRow(i)} className="text-red-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#F7F2E8]"><td colSpan={3} className="px-2 py-2 text-right uc-label">Total Ingredient Cost</td><td className="px-2 py-2 text-right font-semibold">{inr(previewIngCost)}</td><td></td></tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div>
              <div className="uc-label mb-2">Production Costs (per batch)</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Labor (₹)</Label><Input type="number" value={recipe.labor_cost} onChange={(e) => setRecipe({ ...recipe, labor_cost: e.target.value })} data-testid="recipes-labor-input" /></div>
                <div><Label>Overhead (₹)</Label><Input type="number" value={recipe.overhead_cost} onChange={(e) => setRecipe({ ...recipe, overhead_cost: e.target.value })} data-testid="recipes-overhead-input" /></div>
                <div><Label>Packaging (₹)</Label><Input type="number" value={recipe.packaging_cost} onChange={(e) => setRecipe({ ...recipe, packaging_cost: e.target.value })} data-testid="recipes-packaging-input" /></div>
              </div>
              <div className="text-xs text-[#8B7355] mt-1">Total production cost: <b>{inr(previewProdCost)}</b></div>
            </div>

            <div><Label>Process Notes</Label><Textarea rows={3} value={recipe.process_notes || ""} onChange={(e) => setRecipe({ ...recipe, process_notes: e.target.value })} placeholder="Mixing, baking temperature & time, decoration steps…" /></div>

            <div className="bg-gradient-to-br from-[#FEEBC8] to-[#F6AD55]/40 border border-[#F6AD55] rounded-lg p-5 space-y-2">
              <div className="uc-label !text-[#7B341E]">Product Cost Summary</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="uc-label">Ingredient</div><div className="font-[Outfit] font-semibold text-base">{inr(previewIngCost)}</div></div>
                <div><div className="uc-label">Production</div><div className="font-[Outfit] font-semibold text-base">{inr(previewProdCost)}</div></div>
                <div><div className="uc-label">Total / Batch</div><div className="font-[Outfit] font-semibold text-base">{inr(previewTotal)}</div></div>
                <div><div className="uc-label">Cost per Unit</div><div className="font-[Outfit] font-semibold text-xl text-[#C05621]">{inr(previewPerUnit)}</div></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm pt-2 border-t border-[#F6AD55]/60">
                <div><div className="uc-label">Sale Price</div><div className="font-[Outfit] font-semibold text-base">{inr(editorFor?.sale_price || 0)}</div></div>
                <div>
                  <div className="uc-label">Margin (₹)</div>
                  <div className={`font-[Outfit] font-semibold text-lg ${previewMargin >= 0 ? "text-emerald-700" : "text-red-600"}`}>{inr(previewMargin)}</div>
                </div>
                <div>
                  <div className="uc-label">Margin %</div>
                  <div className={`font-[Outfit] font-semibold text-lg ${previewMarginPct >= 30 ? "text-emerald-700" : previewMarginPct >= 0 ? "text-amber-700" : "text-red-600"}`}>{previewMarginPct.toFixed(1)}%</div>
                </div>
              </div>
              {previewMarginPct < 30 && (
                <div className="flex items-center gap-1 text-xs text-[#7B341E]"><Sparkles className="w-3 h-3" /> {previewMarginPct < 0 ? "Selling below cost — reprice or reduce costs." : "Margin under 30% — consider raising sale price or optimizing ingredients."}</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorFor(null)}>Close</Button>
            <Button onClick={saveRecipe} data-testid="recipes-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save Recipe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
    <div className="uc-label">{label}</div>
    <div className={`font-[Outfit] text-2xl font-semibold mt-1 tracking-tight ${accent || ""}`}>{value}</div>
  </div>
);



function NewRecipeButton({ products, recipes, ingredients, onPick, onProductsChanged, onLoad }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("new");
  const [saving, setSaving] = useState(false);
  const [prod, setProd] = useState({ sku: "", name: "", category: "cake", unit: "pcs", sale_price: 0, mrp: 0, gst_rate: 5, hsn_code: "" });
  const [rec, setRec] = useState({ yield_qty: 1, yield_unit: "pcs", ingredients: [{ ingredient_id: "", quantity: 0, unit: "g" }], process_notes: "", labor_cost: 0, overhead_cost: 0, packaging_cost: 0 });
  const [q, setQ] = useState("");
  const withRecipeIds = new Set(recipes.map((r) => r.product_id));

  const reset = () => {
    setMode("new"); setSaving(false); setQ("");
    setProd({ sku: "", name: "", category: "cake", unit: "pcs", sale_price: 0, mrp: 0, gst_rate: 5, hsn_code: "" });
    setRec({ yield_qty: 1, yield_unit: "pcs", ingredients: [{ ingredient_id: "", quantity: 0, unit: "g" }], process_notes: "", labor_cost: 0, overhead_cost: 0, packaging_cost: 0 });
  };
  const suggestSku = () => {
    if (prod.sku) return prod.sku;
    const base = (prod.name || "SKU").toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 12).replace(/-+$/, "");
    return `${base || "SKU"}-${Math.floor(1000 + Math.random() * 9000)}`;
  };
  const addIng = () => setRec((r) => ({ ...r, ingredients: [...r.ingredients, { ingredient_id: "", quantity: 0, unit: "g" }] }));
  const removeIng = (i) => setRec((r) => ({ ...r, ingredients: r.ingredients.filter((_, x) => x !== i) }));
  const updateIng = (i, k, v) => setRec((r) => { const arr = [...r.ingredients]; arr[i] = { ...arr[i], [k]: v }; return { ...r, ingredients: arr }; });

  const ingCost = rec.ingredients.reduce((s, row) => {
    const ing = ingredients.find((x) => x.id === row.ingredient_id);
    if (!ing) return s;
    return s + Number(row.quantity || 0) * Number(ing.cost_per_unit || 0);
  }, 0);
  const prodCost = Number(rec.labor_cost || 0) + Number(rec.overhead_cost || 0) + Number(rec.packaging_cost || 0);
  const totalCost = ingCost + prodCost;
  const perUnit = totalCost / Math.max(Number(rec.yield_qty) || 1, 1);
  const salePrice = Number(prod.sale_price || 0);
  const margin = salePrice - perUnit;
  const marginPct = salePrice ? (margin / salePrice) * 100 : 0;
  const validRecipe = rec.ingredients.some((r) => r.ingredient_id && Number(r.quantity) > 0);

  const saveAll = async (openEditorAfter = false) => {
    if (!prod.name.trim()) return toast.error("Product name required");
    if (!openEditorAfter && !validRecipe) return toast.error("Add at least one ingredient — or click 'Save & configure later'");
    setSaving(true);
    try {
      const productPayload = { ...prod, sku: prod.sku.trim() || suggestSku(), mrp: Number(prod.mrp || prod.sale_price) || 0, sale_price: Number(prod.sale_price) || 0, gst_rate: Number(prod.gst_rate) || 0 };
      const r1 = await api.post("/products", productPayload);
      const newProd = r1.data;
      if (!openEditorAfter && validRecipe) {
        const validIng = rec.ingredients.filter((x) => x.ingredient_id && Number(x.quantity) > 0);
        const r2 = await api.put(`/products/${newProd.id}/recipe`, {
          yield_qty: Number(rec.yield_qty) || 1,
          yield_unit: rec.yield_unit,
          ingredients: validIng.map((x) => ({ ingredient_id: x.ingredient_id, quantity: Number(x.quantity), unit: x.unit })),
          process_notes: rec.process_notes,
          labor_cost: Number(rec.labor_cost) || 0,
          overhead_cost: Number(rec.overhead_cost) || 0,
          packaging_cost: Number(rec.packaging_cost) || 0,
        });
        toast.success(`"${newProd.name}" ready — Cost/unit ₹${r2.data.cost_per_unit} · Margin ${r2.data.margin_pct}%`);
        setOpen(false); reset();
        onLoad?.();
        return;
      }
      toast.success(`Product "${newProd.name}" created — configure recipe now`);
      setOpen(false); reset();
      await onProductsChanged?.();
      onPick(newProd);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    return !s ? products : products.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s) || (p.category || "").toLowerCase().includes(s));
  }, [products, q]);
  const pickExisting = (p) => { setOpen(false); reset(); onPick(p); };

  const CATEGORIES = ["cake", "bread", "biscuit", "cookie", "pastry", "chocolate", "savoury", "combo", "other"];
  const UNITS_ = ["pcs", "kg", "g", "dozen", "box"];

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="recipes-new-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
          <Plus className="w-4 h-4 mr-1" /> New Recipe
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl">Create New Recipe</DialogTitle>
        </DialogHeader>

        <div className="flex gap-0 mb-1 border border-[#EAE5D9] rounded-md p-0.5 bg-[#FAF6EE] w-fit">
          <button type="button" onClick={() => setMode("new")} data-testid="recipes-mode-new"
            className={`px-3 py-1.5 rounded text-xs font-medium ${mode === "new" ? "bg-[#C05621] text-white" : "text-[#5C4033] hover:bg-white"}`}>
            <Sparkles className="w-3.5 h-3.5 inline mr-1" /> Brand-new product
          </button>
          <button type="button" onClick={() => setMode("existing")} data-testid="recipes-mode-existing"
            className={`px-3 py-1.5 rounded text-xs font-medium ${mode === "existing" ? "bg-[#C05621] text-white" : "text-[#5C4033] hover:bg-white"}`}>
            <Search className="w-3.5 h-3.5 inline mr-1" /> Existing product
          </button>
        </div>

        {mode === "new" ? (
          <div className="space-y-3">
            <details open className="border border-[#EAE5D9] rounded-md">
              <summary className="cursor-pointer px-3 py-2 bg-emerald-50 text-emerald-800 text-sm font-semibold flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] flex items-center justify-center">1</span> Product SKU
              </summary>
              <div className="p-3 grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Product Name *</Label><Input value={prod.name} onChange={(e) => setProd({ ...prod, name: e.target.value })} placeholder="e.g. Blueberry Cheesecake 500g" data-testid="recipes-new-prod-name" autoFocus /></div>
                <div><Label>SKU</Label><Input value={prod.sku} onChange={(e) => setProd({ ...prod, sku: e.target.value })} placeholder={`auto: ${suggestSku()}`} data-testid="recipes-new-prod-sku" /></div>
                <div><Label>Category</Label>
                  <Select value={prod.category} onValueChange={(v) => setProd({ ...prod, category: v })}>
                    <SelectTrigger data-testid="recipes-new-prod-cat"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Unit</Label>
                  <Select value={prod.unit} onValueChange={(v) => setProd({ ...prod, unit: v })}>
                    <SelectTrigger data-testid="recipes-new-prod-unit"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{UNITS_.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>HSN</Label><Input value={prod.hsn_code} onChange={(e) => setProd({ ...prod, hsn_code: e.target.value })} placeholder="e.g. 1905" /></div>
                <div><Label>Sale Price / MRP (₹)</Label><Input type="number" value={prod.sale_price} onChange={(e) => setProd({ ...prod, sale_price: e.target.value, mrp: e.target.value })} data-testid="recipes-new-prod-price" /></div>
                <div><Label>GST %</Label><Input type="number" step="0.5" value={prod.gst_rate} onChange={(e) => setProd({ ...prod, gst_rate: e.target.value })} /></div>
              </div>
            </details>

            <details open className="border border-[#EAE5D9] rounded-md">
              <summary className="cursor-pointer px-3 py-2 bg-blue-50 text-blue-800 text-sm font-semibold flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center">2</span> Recipe · Ingredients · Costs
              </summary>
              <div className="p-3 space-y-3">
                <div className="grid grid-cols-5 gap-2">
                  <div><Label>Yield Qty</Label><Input type="number" value={rec.yield_qty} onChange={(e) => setRec({ ...rec, yield_qty: e.target.value })} data-testid="recipes-new-yield" /></div>
                  <div><Label>Yield Unit</Label>
                    <Select value={rec.yield_unit} onValueChange={(v) => setRec({ ...rec, yield_unit: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{UNITS_.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Labour ₹</Label><Input type="number" value={rec.labor_cost} onChange={(e) => setRec({ ...rec, labor_cost: e.target.value })} /></div>
                  <div><Label>Overhead ₹</Label><Input type="number" value={rec.overhead_cost} onChange={(e) => setRec({ ...rec, overhead_cost: e.target.value })} /></div>
                  <div><Label>Packing ₹</Label><Input type="number" value={rec.packaging_cost} onChange={(e) => setRec({ ...rec, packaging_cost: e.target.value })} /></div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1"><Label>Ingredients</Label><div className="flex gap-1"><Button size="sm" variant="outline" onClick={addIng} data-testid="recipes-new-ing-add"><Plus className="w-3 h-3 mr-1" /> Add row</Button><QuickAddIngredient label="Master" onAdded={async (ing) => { await onLoad?.(); setRec((r) => { const arr = [...r.ingredients]; const emptyIdx = arr.findIndex((x) => !x.ingredient_id); if (emptyIdx >= 0) arr[emptyIdx] = { ingredient_id: ing.id, quantity: 0, unit: ing.unit || "g" }; else arr.push({ ingredient_id: ing.id, quantity: 0, unit: ing.unit || "g" }); return { ...r, ingredients: arr }; }); }} /></div></div>
                  <div className="border border-[#EAE5D9] rounded max-h-56 overflow-y-auto" data-testid="recipes-new-ing-list">
                    {rec.ingredients.map((row, i) => {
                      const ing = ingredients.find((x) => x.id === row.ingredient_id);
                      const cost = ing ? Number(row.quantity || 0) * Number(ing.cost_per_unit || 0) : 0;
                      return (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border-b border-[#EAE5D9] last:border-0">
                          <div className="col-span-5">
                            <Select value={row.ingredient_id} onValueChange={(v) => updateIng(i, "ingredient_id", v)}>
                              <SelectTrigger data-testid={`recipes-new-ing-${i}-select`}><SelectValue placeholder="Pick ingredient" /></SelectTrigger>
                              <SelectContent className="bg-white max-h-72">
                                {ingredients.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} <span className="text-xs text-[#8B7355]">· ₹{x.cost_per_unit}/{x.unit}</span></SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <Input className="col-span-2" type="number" step="0.01" value={row.quantity} onChange={(e) => updateIng(i, "quantity", e.target.value)} placeholder="qty" data-testid={`recipes-new-ing-${i}-qty`} />
                          <div className="col-span-2">
                            <Select value={row.unit} onValueChange={(v) => updateIng(i, "unit", v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-white">{UNITS_.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2 text-xs text-right font-mono">{ing ? `₹${cost.toFixed(2)}` : <span className="text-[#8B7355]">—</span>}</div>
                          <button onClick={() => removeIng(i)} className="col-span-1 text-red-500 justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div><Label>Process notes (optional)</Label><Textarea rows={2} value={rec.process_notes} onChange={(e) => setRec({ ...rec, process_notes: e.target.value })} /></div>

                <div className="grid grid-cols-4 gap-2 text-center text-xs bg-[#FAF6EE] border border-[#EAE5D9] rounded p-2" data-testid="recipes-new-preview">
                  <div><div className="uc-label">Ingredient</div><div className="font-semibold text-[#2C1810]">₹{ingCost.toFixed(2)}</div></div>
                  <div><div className="uc-label">Prod. cost</div><div className="font-semibold text-[#2C1810]">₹{prodCost.toFixed(2)}</div></div>
                  <div><div className="uc-label">Cost / unit</div><div className="font-semibold text-orange-700">₹{perUnit.toFixed(2)}</div></div>
                  <div><div className="uc-label">Margin</div><div className={`font-semibold ${margin < 0 ? "text-red-700" : marginPct < 30 ? "text-amber-700" : "text-emerald-700"}`}>₹{margin.toFixed(2)} <span className="text-[10px]">({marginPct.toFixed(1)}%)</span></div></div>
                </div>
              </div>
            </details>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-800">Pick an existing product SKU. If it already has a recipe, a new version will be created.</div>
            <div className="flex items-center gap-2 bg-[#FAF6EE] border border-[#EAE5D9] rounded px-2 py-1.5">
              <Search className="w-4 h-4 text-[#8B7355]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, SKU or category…" className="flex-1 bg-transparent outline-none text-sm" data-testid="recipes-new-search" autoFocus />
            </div>
            <div className="max-h-72 overflow-y-auto border border-[#EAE5D9] rounded-md" data-testid="recipes-new-product-list">
              {filtered.length === 0 && <div className="p-3 text-sm text-[#8B7355]">No products match "{q}".</div>}
              {filtered.map((p) => {
                const has = withRecipeIds.has(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => pickExisting(p)} data-testid={`recipes-new-pick-${p.id}`} className="w-full text-left px-3 py-2 hover:bg-[#FAF6EE] flex items-center justify-between border-b border-[#EAE5D9] last:border-0">
                    <div>
                      <div className="text-sm font-medium text-[#2C1810]">{p.name}</div>
                      <div className="text-xs text-[#8B7355]">SKU {p.sku}{p.category ? ` · ${p.category}` : ""}</div>
                    </div>
                    {has ? <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px]">has recipe — new version</Badge> : <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">no recipe yet</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
          {mode === "new" && (
            <>
              <Button variant="outline" onClick={() => saveAll(true)} disabled={saving || !prod.name.trim()} data-testid="recipes-new-later">Save & configure later</Button>
              <Button onClick={() => saveAll(false)} disabled={saving || !prod.name.trim()} data-testid="recipes-new-save" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                {saving ? "Saving…" : "Save Product + Recipe"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

